/**
 * WeekDaySelector Component
 * Allows users to select multiple days of the week
 *
 * @version 2025.1.0
 */
import React from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { WeekDay, WEEK_DAY_NAMES, WEEK_DAY_SHORT_NAMES } from '../../../types/professionalSchedule';

interface WeekDaySelectorProps {
  selectedDays: WeekDay[];
  onChange: (days: WeekDay[]) => void;
  disabled?: boolean;
  showLabels?: boolean;
  orientation?: 'horizontal' | 'vertical';
}

const WeekDaySelector: React.FC<WeekDaySelectorProps> = ({
  selectedDays,
  onChange,
  disabled = false,
  showLabels = true,
  orientation = 'horizontal',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleChange = (_event: React.MouseEvent<HTMLElement>, newDays: WeekDay[]) => {
    // Sort days to maintain order
    const sortedDays = newDays.sort((a, b) => a - b);
    onChange(sortedDays);
  };

  const weekDays: WeekDay[] = [
    WeekDay.SUNDAY,
    WeekDay.MONDAY,
    WeekDay.TUESDAY,
    WeekDay.WEDNESDAY,
    WeekDay.THURSDAY,
    WeekDay.FRIDAY,
    WeekDay.SATURDAY,
  ];

  return (
    <Box>
      {showLabels && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Selecione os dias da semana
        </Typography>
      )}
      <ToggleButtonGroup
        value={selectedDays}
        onChange={handleChange}
        aria-label="dias da semana"
        disabled={disabled}
        orientation={isMobile ? 'vertical' : orientation}
        sx={{
          display: 'flex',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
          gap: 0.5,
          '& .MuiToggleButton-root': {
            flex: isMobile ? 1 : 'none',
            minWidth: isMobile ? '100%' : 80,
            py: 1.5,
            px: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            },
          },
        }}
      >
        {weekDays.map((day) => (
          <ToggleButton
            key={day}
            value={day}
            aria-label={WEEK_DAY_NAMES[day]}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" fontWeight="bold">
                {isMobile ? WEEK_DAY_NAMES[day] : WEEK_DAY_SHORT_NAMES[day]}
              </Typography>
            </Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

export default WeekDaySelector;
